// 클라이언트 사이드 전용 ExcelHelper
import ExcelJS from "exceljs";

/**
 * Excel 파일 생성을 위한 헬퍼 라이브러리 (클라이언트 사이드 전용)
 * ExcelJS를 사용한 공통 기능들을 제공합니다.
 */
class ExcelHelperClient {
    constructor() {
        // 상수 정의
        this.CONSTANTS = {
            DEFAULT_COLUMN_WIDTH: 15,
            MIN_COLUMN_WIDTH: 8,
            MAX_COLUMN_WIDTH: 50,
            ROTATE_THRESHOLD: 10,
            MAX_ROWS_FOR_AUTOFIT: 100,
            EXCLUDE_COLUMNS: ["Date", "Time", "날짜", "시간"],
            DEFAULT_CREATOR: "THEKIE",
            FONT_FAMILY: "맑은 고딕",
            HEADER_FONT_SIZE: 11,
            DATA_FONT_SIZE: 10,
            ROTATED_HEADER_WIDTH: 10,
        };

        // 공통 스타일 정의
        this.STYLES = this.createStyles();
    }

    /**
     * 공통 스타일을 생성합니다.
     * @returns {Object} 스타일 객체
     */
    createStyles() {
        const borderStyle = {
            top: { style: "thin", color: { argb: "FFAAAAAA" } },
            left: { style: "thin", color: { argb: "FFAAAAAA" } },
            bottom: { style: "thin", color: { argb: "FFAAAAAA" } },
            right: { style: "thin", color: { argb: "FFAAAAAA" } },
        };

        return {
            BORDER: borderStyle,
            HEADER: {
                font: {
                    bold: true,
                    color: { argb: "FFFFFFFF" },
                    name: this.CONSTANTS.FONT_FAMILY,
                    size: this.CONSTANTS.HEADER_FONT_SIZE,
                },
                fill: {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FF366092" },
                },
                alignment: { horizontal: "center", vertical: "middle" },
                border: borderStyle,
            },
            DATA_ROW: {
                font: {
                    bold: false,
                    color: { argb: "FF000000" },
                    name: this.CONSTANTS.FONT_FAMILY,
                    size: this.CONSTANTS.DATA_FONT_SIZE,
                },
                alignment: { horizontal: "center", vertical: "middle" },
                border: borderStyle,
            },
        };
    }

    /**
     * 새로운 워크북을 생성하고 기본 메타데이터를 설정합니다.
     * @param {Object} options - 워크북 옵션
     * @param {string} options.creator - 생성자명
     * @param {string} options.lastModifiedBy - 최종 수정자
     * @returns {ExcelJS.Workbook} 생성된 워크북
     */
    createWorkbook(options = {}) {
        const workbook = new ExcelJS.Workbook();
        const now = new Date();

        // 메타데이터 설정
        workbook.creator = options.creator || this.CONSTANTS.DEFAULT_CREATOR;
        workbook.lastModifiedBy = options.lastModifiedBy || this.CONSTANTS.DEFAULT_CREATOR;
        workbook.created = now;
        workbook.modified = now;

        return workbook;
    }

    /**
     * 워크시트를 생성하고 헤더를 설정합니다.
     * @param {ExcelJS.Workbook} workbook - 워크북 객체
     * @param {string} sheetName - 시트명
     * @param {Array} headers - 헤더 배열
     * @param {Object} options - 옵션
     * @param {boolean} options.freezeHeader - 첫 행(헤더) 고정 여부
     * @returns {ExcelJS.Worksheet} 생성된 워크시트
     */
    createWorksheetWithHeaders(workbook, sheetName, headers, options = {}) {
        const worksheetOptions = {};
        if (options.freezeHeader) {
            worksheetOptions.views = [{ state: "frozen", ySplit: 1, activeCell: "A2", showGridLines: true }];
        }
        const worksheet = workbook.addWorksheet(sheetName, worksheetOptions);

        // 컬럼 설정
        const columns = this.createColumnDefinitions(headers, options);
        worksheet.columns = columns;

        // 헤더 스타일 적용
        this.applyHeaderStyle(worksheet);

        return worksheet;
    }

    /**
     * 컬럼 정의를 생성합니다.
     * @param {Array} headers - 헤더 배열
     * @param {Object} options - 옵션
     * @returns {Array} 컬럼 정의 배열
     */
    createColumnDefinitions(headers, options = {}) {
        return headers.map((header) => ({
            header,
            key: header,
            width: options.columnWidths?.[header] || this.getDefaultColumnWidth(header),
        }));
    }

    /**
     * 헤더 행에 기본 스타일을 적용합니다.
     * @param {ExcelJS.Worksheet} worksheet - 워크시트 객체
     */
    applyHeaderStyle(worksheet) {
        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
            // 헤더 스타일 적용
            cell.border = this.STYLES.HEADER.border;
            cell.font = this.STYLES.HEADER.font;
            cell.fill = this.STYLES.HEADER.fill;
            cell.alignment = this.STYLES.HEADER.alignment;
        });
    }

    /**
     * 데이터 행에 스타일을 적용합니다.
     * @param {ExcelJS.Worksheet} worksheet - 워크시트 객체
     * @param {number} rowIndex - 행 인덱스 (1부터 시작)
     * @param {Object} options - 스타일 옵션
     * @param {boolean} options.alternatingColors - 교대로 배경색 적용 여부
     */
    applyDataRowStyle(worksheet, rowIndex, options = {}) {
        const dataRow = worksheet.getRow(rowIndex);
        const isEvenRow = (rowIndex - 1) % 2 === 0;

        dataRow.eachCell((cell) => {
            // 기본 스타일 적용
            cell.border = this.STYLES.DATA_ROW.border;
            cell.font = this.STYLES.DATA_ROW.font;
            cell.alignment = this.STYLES.DATA_ROW.alignment;

            // 교대로 배경색 적용
            if (options.alternatingColors) {
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: isEvenRow ? "FFFFFFFF" : "FFF8F9FA" },
                };
            }
        });
    }

    /**
     * 데이터를 워크시트에 추가하고 스타일을 적용합니다.
     * @param {ExcelJS.Worksheet} worksheet - 워크시트 객체
     * @param {Array} data - 추가할 데이터 배열
     * @param {Object} options - 옵션
     * @param {boolean} options.alternatingColors - 교대로 배경색 적용 여부
     * @param {Object} options.cellTypes - 셀 타입 설정 (헤더명: "string" 또는 "number")
     */
    addDataWithStyle(worksheet, data, options = {}) {
        data.forEach((rowData) => {
            // 헤더 순서에 맞게 데이터 재정렬
            const orderedRowData = this.reorderRowDataByHeaders(worksheet, rowData);
            const dataRow = worksheet.addRow(orderedRowData);

            // 셀 타입 및 형식 적용
            this.applyCellTypesAndFormats(worksheet, dataRow, options);

            this.applyDataRowStyle(worksheet, dataRow.number, options);
        });
    }

    /**
     * 셀 타입과 형식을 적용합니다.
     * @param {ExcelJS.Worksheet} worksheet - 워크시트 객체
     * @param {ExcelJS.Row} dataRow - 데이터 행
     * @param {Object} options - 옵션
     * @param {Object} options.cellTypes - 셀 타입 설정 (헤더명: "string" 또는 "number")
     */
    applyCellTypesAndFormats(worksheet, dataRow, options = {}) {
        const { cellTypes = {} } = options;

        // 각 컬럼에 대해 타입과 형식 적용
        worksheet.columns.forEach((column, index) => {
            const header = column.header;
            const cell = dataRow.getCell(index + 1);

            // 셀 타입 및 형식 적용
            if (cellTypes[header]) {
                this.applyCellType(cell, cellTypes[header]);
            }
        });
    }

    /**
     * 셀에 타입을 적용합니다.
     * @param {ExcelJS.Cell} cell - 셀 객체
     * @param {string} cellType - 셀 타입 ("string" 또는 "number")
     */
    applyCellType(cell, cellType) {
        if (typeof cellType === "string") {
            switch (cellType.toLowerCase()) {
                case "number":
                case "num":
                    cell.value = parseFloat(cell.value) || 0;
                    break;
                case "string":
                case "text":
                default:
                    cell.value = String(cell.value || "");
                    break;
            }
        }
    }

    /**
     * 헤더명에 따른 기본 컬럼 너비를 반환합니다.
     * @param {string} header - 헤더명
     * @returns {number} 컬럼 너비
     */
    getDefaultColumnWidth(header) {
        // 특정 헤더에 대한 최소 너비 매핑
        const specificWidths = {
            Date: 12,
            Time: 10,
            계측기명: 12,
            서브모듈명: 25,
            피더번호: 10,
            속성명: 15,
            에너지원: 10,
            공급구분: 10,
            사용용도: 12,
            적용구역: 12,
            제조사: 15,
            모델명: 15,
            "시리얼 넘버": 15,
            정격용량: 12,
            용량단위: 10,
            정격COP: 10,
            설치장소: 20,
            설명: 30,
            수정일자: 20,
            보정: 8,
            시간: 12,
            집계값: 15,
            원본값: 15,
            이전값: 15,
        };

        const width = specificWidths[header] ? specificWidths[header] : this.CONSTANTS.DEFAULT_COLUMN_WIDTH;

        return width;
    }

    /**
     * 헤더 순서에 맞게 행 데이터를 재정렬합니다.
     * @param {ExcelJS.Worksheet} worksheet - 워크시트 객체
     * @param {Object} rowData - 원본 행 데이터
     * @returns {Array} 재정렬된 행 데이터 배열
     */
    reorderRowDataByHeaders(worksheet, rowData) {
        const orderedData = [];

        // 워크시트의 컬럼 순서에 따라 데이터 재정렬
        worksheet.columns.forEach((column) => {
            const header = column.header;
            const value = rowData[header];

            // 값이 존재하면 사용, 없으면 빈 문자열
            orderedData.push(value !== undefined ? value : "");
        });

        return orderedData;
    }

    /**
     * 클라이언트 사이드에서 워크북을 Blob으로 변환합니다.
     * @param {ExcelJS.Workbook} workbook - 워크북 객체
     * @returns {Promise<Blob>} Blob 객체
     */
    async workbookToBlob(workbook) {
        try {
            const buffer = await workbook.xlsx.writeBuffer();
            return new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
        } catch (err) {
            console.error("ExcelHelper", "워크북을 Blob으로 변환 실패:", err);
            throw new Error(`엑셀 파일 생성 실패: ${err?.message || ""}`);
        }
    }

    /**
     * 클라이언트 사이드에서 파일 다운로드를 처리합니다.
     * @param {Blob} blob - 다운로드할 Blob 객체
     * @param {string} fileName - 다운로드될 파일명
     */
    downloadBlob(blob, fileName) {
        try {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("ExcelHelper", "파일 다운로드 중 오류 발생:", err);
            throw new Error("파일 다운로드 중 오류가 발생했습니다.");
        }
    }

    /**
     * 그룹 헤더(2행)가 있는 워크북을 생성하고 다운로드합니다.
     * @param {Array} data - 데이터 배열
     * @param {Array} columnDefs - 컬럼 정의 [{ key, header, group?, width? }]
     *   - key: 데이터 객체의 키
     *   - header: 2행 서브헤더 라벨
     *   - group: 1행 그룹 헤더 라벨 (같은 group이 연속되면 병합)
     *   - width: 컬럼 너비 (선택)
     * @param {string} fileName - 파일명
     * @param {Object} options - createAndDownloadWorkbook과 동일
     * @param {string[]} options.autoFilterColumns - 필터를 적용할 컬럼 key 배열 (미지정 시 전체)
     */
    async createAndDownloadWorkbookWithGroupedHeaders(data, columnDefs, fileName, options = {}) {
        try {
            const workbook = this.createWorkbook(options);
            const sheetName = options.sheetName || "Sheet1";

            const worksheetOptions = {};
            if (options.freezeHeader) {
                worksheetOptions.views = [{ state: "frozen", ySplit: 2, activeCell: "A3", showGridLines: true }];
            }
            const worksheet = workbook.addWorksheet(sheetName, worksheetOptions);

            const keys = columnDefs.map((c) => c.key);
            const colWidths = options.columnWidths || {};
            worksheet.columns = keys.map((key, i) => ({
                key,
                header: key,
                width: colWidths[key] ?? columnDefs[i].width ?? this.CONSTANTS.DEFAULT_COLUMN_WIDTH,
            }));

            const row1 = worksheet.getRow(1);
            const row2 = worksheet.getRow(2);
            let mergeRanges = [];

            let col = 1;
            let groupStart = null;
            let groupLabel = null;

            columnDefs.forEach((def) => {
                const cell1 = row1.getCell(col);
                const cell2 = row2.getCell(col);

                if (def.group) {
                    if (groupLabel !== def.group) {
                        if (groupStart != null) {
                            mergeRanges.push({ start: groupStart, end: col - 1 });
                        }
                        groupStart = col;
                        groupLabel = def.group;
                    }
                    cell1.value = def.group;
                    cell2.value = def.header;
                } else {
                    if (groupStart != null) {
                        mergeRanges.push({ start: groupStart, end: col - 1 });
                        groupStart = null;
                        groupLabel = null;
                    }
                    cell1.value = def.header;
                    worksheet.mergeCells(1, col, 2, col);
                }
                col++;
            });
            if (groupStart != null) {
                mergeRanges.push({ start: groupStart, end: col - 1 });
            }

            mergeRanges.forEach(({ start, end }) => {
                if (end > start) {
                    worksheet.mergeCells(1, start, 1, end);
                }
            });

            [row1, row2].forEach((r) => {
                r.eachCell((cell) => {
                    cell.border = this.STYLES.HEADER.border;
                    cell.font = this.STYLES.HEADER.font;
                    cell.fill = this.STYLES.HEADER.fill;
                    cell.alignment = this.STYLES.HEADER.alignment;
                });
            });

            data.forEach((rowData) => {
                const orderedRowData = keys.map((k) => (rowData[k] !== undefined ? rowData[k] : ""));
                const dataRow = worksheet.addRow(orderedRowData);

                if (options.cellTypes) {
                    keys.forEach((k, idx) => {
                        const cell = dataRow.getCell(idx + 1);
                        if (options.cellTypes[k]) {
                            this.applyCellType(cell, options.cellTypes[k]);
                        }
                    });
                }
                this.applyDataRowStyle(worksheet, dataRow.number, options);
            });

            if (options.autoFilter && data.length > 0) {
                const lastRow = 2 + data.length;
                let fromCol = 1;
                let toCol = keys.length;
                if (Array.isArray(options.autoFilterColumns) && options.autoFilterColumns.length > 0) {
                    const indices = options.autoFilterColumns
                        .map((k) => keys.indexOf(k) + 1)
                        .filter((i) => i > 0);
                    if (indices.length > 0) {
                        fromCol = Math.min(...indices);
                        toCol = Math.max(...indices);
                    }
                }
                worksheet.autoFilter = {
                    from: { row: 1, column: fromCol },
                    to: { row: lastRow, column: toCol },
                };
            }

            const blob = await this.workbookToBlob(workbook);
            this.downloadBlob(blob, fileName);
        } catch (error) {
            console.error("ExcelHelper", "그룹 헤더 워크북 생성 및 다운로드 실패:", error);
            throw error;
        }
    }

    /**
     * 클라이언트 사이드에서 워크북을 생성하고 다운로드합니다.
     * @param {Array} data - 데이터 배열
     * @param {Array} headers - 헤더 배열
     * @param {string} fileName - 파일명
     * @param {Object} options - 옵션
     * @param {string} options.sheetName - 시트명 (기본값: "Sheet1")
     * @param {string} options.creator - 생성자명
     * @param {string} options.lastModifiedBy - 최종 수정자
     * @param {Object} options.columnWidths - 컬럼 너비 설정
     * @param {boolean} options.alternatingColors - 교대로 배경색 적용 여부 (기본값: true)
     * @param {boolean} options.autoFitColumns - 컬럼 자동 크기 조정 여부 (기본값: true)
     * @param {boolean} options.allowUserResize - 사용자 컬럼 크기 조정 허용 (기본값: true)
     * @param {Object} options.cellTypes - 셀 타입 설정 (헤더명: "string" 또는 "number")
     * @param {boolean} options.freezeHeader - 헤더 행 고정 여부 (기본값: false)
     * @param {boolean} options.autoFilter - 자동 필터 적용 여부 (기본값: false)
     */
    async createAndDownloadWorkbook(data, headers, fileName, options = {}) {
        try {
            // 워크북 생성
            const workbook = this.createWorkbook(options);

            // 시트명 설정 (기본값: "Sheet1")
            const sheetName = options.sheetName || "Sheet1";

            // 워크시트 생성 (헤더 고정 옵션 전달)
            const worksheet = this.createWorksheetWithHeaders(workbook, sheetName, headers, {
                autoFitColumns: true,
                allowUserResize: true,
                freezeHeader: options.freezeHeader,
                ...options,
            });

            // 데이터 추가
            this.addDataWithStyle(worksheet, data, {
                alternatingColors: true,
                autoFitColumns: true,
                allowUserResize: true,
                ...options,
            });

            // 자동 필터 적용
            if (options.autoFilter && data.length > 0) {
                const lastRow = 1 + data.length;
                const lastCol = headers.length;
                worksheet.autoFilter = {
                    from: { row: 1, column: 1 },
                    to: { row: lastRow, column: lastCol },
                };
            }

            // Blob으로 변환
            const blob = await this.workbookToBlob(workbook);

            // 다운로드
            this.downloadBlob(blob, fileName);
        } catch (error) {
            console.error("ExcelHelper", "워크북 생성 및 다운로드 실패:", error);
            throw error;
        }
    }

    /**
     * 여러 시트로 구성된 워크북을 생성하고 다운로드합니다.
     * @param {Array} sheetConfigs - 시트 설정 배열
     * @param {string} sheetConfigs[].name - 시트명
     * @param {Array} sheetConfigs[].headers - 헤더 배열
     * @param {Array} sheetConfigs[].data - 데이터 배열
     * @param {Object} sheetConfigs[].options - 시트별 옵션
     * @param {string} fileName - 파일명
     * @param {Object} options - 전체 워크북 옵션
     * @param {string} options.creator - 생성자명
     * @param {string} options.lastModifiedBy - 최종 수정자
     */
    async createAndDownloadMultiSheetWorkbook(sheetConfigs, fileName, options = {}) {
        try {
            // 워크북 생성
            const workbook = this.createWorkbook(options);

            // 각 시트 생성
            sheetConfigs.forEach((config) => {
                const worksheet = this.createWorksheetWithHeaders(workbook, config.name, config.headers, {
                    autoFitColumns: true,
                    allowUserResize: true,
                    ...config.options,
                });

                // 데이터 추가
                this.addDataWithStyle(worksheet, config.data, {
                    alternatingColors: true,
                    autoFitColumns: true,
                    allowUserResize: true,
                    ...config.options,
                });
            });

            // Blob으로 변환
            const blob = await this.workbookToBlob(workbook);

            // 다운로드
            this.downloadBlob(blob, fileName);
        } catch (error) {
            console.error("ExcelHelper", "다중 시트 워크북 생성 및 다운로드 실패:", error);
            throw error;
        }
    }
}

export default new ExcelHelperClient();
